import { Injectable, OnDestroy } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { Observable, of, Subject } from "rxjs";
import {
  filter,
  startWith,
  withLatestFrom,
  switchMap,
  map,
  takeUntil,
  take,
  catchError,
} from "rxjs/operators";
import { CommunityService } from "../../modules/community/community.service";
import { AuthService } from "../../core/authentication/services/auth.service";
import { CommunityBase } from "../../shared/enums/community-base.enum";

export enum NavItems {
  None,
  Home,
  Events,
  Communities,
  Networks,
  Manage,
}

@Injectable({
  providedIn: "root",
})
export class ActiveNavItemService implements OnDestroy {
  private ngUnsubscribe = new Subject();

  public activeNavItem$: Observable<NavItems> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(new NavigationEnd(0, this.router.url, this.router.url)),
    withLatestFrom(this.authService.isLoading$),
    filter(([_, isLoading]) => !isLoading),
    map(([navEvent]) => navEvent.url),
    filter((url): url is string => !!url),
    switchMap((url) => {
      const path = this.removeQueryParams(url);

      if (this.ignoreRoutes(path)) {
        return of(NavItems.None);
      }

      if (this.isCommunityOrNetworkActivated(path)) {
        const communityId = this.extractIdFromUrl(path);
        if (communityId === null) {
          return of(NavItems.None);
        }

        return this.communityService.getCommunityData(communityId).pipe(
          take(1),
          map((community) =>
            community.communityBaseId === CommunityBase.network
              ? NavItems.Networks
              : NavItems.Communities
          ),
          catchError(() => {
            return of(NavItems.None);
          })
        );
      }

      if (this.isHomeActivated(url)) {
        return of(NavItems.Home);
      }
      if (this.isEventActivated(url)) {
        return of(NavItems.Events);
      }
      if (this.isCommunityActivated(url)) {
        return of(NavItems.Communities);
      }
      if (this.isNetworkActivated(url)) {
        return of(NavItems.Networks);
      }
      if (this.isManageActivated(url)) {
        return of(NavItems.Manage);
      }

      return of(NavItems.None);
    }),
    takeUntil(this.ngUnsubscribe)
  );

  constructor(
    private router: Router,
    private communityService: CommunityService,
    private authService: AuthService
  ) {}

  // Removes everything after a "?" so that query parameters are ignored.
  private removeQueryParams(url: string): string {
    const index = url.indexOf("?");
    return index !== -1 ? url.substring(0, index) : url;
  }

  // Utility functions that encapsulate route checks
  private ignoreRoutes(url: string): boolean {
    const ignorePaths = ["callback"];
    return ignorePaths.some((path) => url.includes(path));
  }

  private isHomeActivated(url: string): boolean {
    return url === "/";
  }

  private isEventActivated(url: string): boolean {
    const paths = ["events", "event-form"];
    return paths.some((path) => url.includes(path));
  }

  private isCommunityActivated(url: string): boolean {
    const paths = ["communities"];
    return paths.some((path) => url.includes(path));
  }

  private isNetworkActivated(url: string): boolean {
    const paths = ["networks"];
    return paths.some((path) => url.includes(path));
  }

  private isCommunityOrNetworkActivated(url: string): boolean {
    const paths = ["community"];
    return paths.some((path) => url.includes(path));
  }

  private isManageActivated(url: string): boolean {
    const paths = ["manage"];
    return paths.some((path) => url.includes(path));
  }

  private extractIdFromUrl(url: string): number | null {
    const path = url.split("?")[0];
    const segments = path.split("/").filter((segment) => segment.trim() !== "");
    const idStr = segments[segments.length - 1];
    const id = parseInt(idStr, 10);
    if (isNaN(id)) {
      return null;
    }
    return id;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }
}
